import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { cn } from "@/lib/utils";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className, once = true }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function SectionHead({ eyebrow, title, sub, align = "center", className }) {
  return (
    <Reveal className={cn("mb-12 sm:mb-16", align === "center" ? "text-center" : "text-left", className)}>
      {eyebrow && <p className="font-mono-label text-[11px] text-accent-bright mb-4">{eyebrow}</p>}
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{title}</h2>
      {sub && <p className={cn("mt-4 text-sm sm:text-base text-mist leading-relaxed", align === "center" && "max-w-2xl mx-auto")}>{sub}</p>}
    </Reveal>
  );
}

export function Counter({ value, suffix = "", className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, value, { duration: 1.8, ease: "easeOut", onUpdate: (v) => setN(Math.round(v)) });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className={className}>
      {n}
      {suffix}
    </span>
  );
}

export function Stars({ rating = 5, className }) {
  return (
    <div className={cn("flex items-center gap-0.5", className)} data-testid="testimonial-stars">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          className={cn("h-4 w-4", i <= Math.round(rating) ? "fill-amber-400" : "fill-white/15")}
        >
          <path d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.9l-5.3 2.7 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
        </svg>
      ))}
    </div>
  );
}
